import { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { User, Edit, Check, X } from 'lucide-react';
import { AdminUserManagementService } from '@/services/admin-user-management.service';
import { toast } from 'sonner';
import { Profile } from '@/types/database.types';
import { USER_ROLES } from '@/types/user-roles';

interface EditableUserTypeSelectorProps {
  user: Profile;
  isProtectedEmail: boolean;
  onUserTypeChange: () => void;
}

export const EditableUserTypeSelector = ({ 
  user, 
  isProtectedEmail,
  onUserTypeChange 
}: EditableUserTypeSelectorProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedType, setSelectedType] = useState(user.user_type || 'regular');
  const [isUpdating, setIsUpdating] = useState(false);

  const getUserTypeDisplay = () => {
    if (isProtectedEmail) {
      return 'Super Admin';
    }
    
    const roleOption = USER_ROLES.find(role => role.value === user.user_type);
    return roleOption?.label || 'Regular User';
  };

  const handleSave = async () => {
    if (selectedType === user.user_type) {
      setIsEditing(false);
      return;
    }

    setIsUpdating(true);
    try {
      await AdminUserManagementService.updateUserType(user.id, selectedType);
      toast.success('User type updated successfully');
      setIsEditing(false);
      onUserTypeChange();
    } catch (error) {
      console.error('Error updating user type:', error);
      toast.error('Failed to update user type');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setSelectedType(user.user_type || 'regular');
    setIsEditing(false);
  };

  if (isProtectedEmail) {
    return (
      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">User Type</span>
        <Button 
          variant="outline" 
          size="sm"
          disabled={true}
          className="capitalize"
        >
          <User className="mr-2 h-4 w-4" />
          Super Admin
        </Button>
      </div>
    );
  }

  return (
    <div className="flex justify-between items-center">
      <span className="text-sm text-muted-foreground">User Type</span>
      
      {isEditing ? (
        <div className="flex items-center gap-2">
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {USER_ROLES.map((role) => (
                <SelectItem key={role.value} value={role.value}>
                  {role.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            disabled={isUpdating}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={isUpdating}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <Button 
          variant="outline" 
          size="sm"
          className="capitalize"
          onClick={() => setIsEditing(true)}
        >
          <User className="mr-2 h-4 w-4" />
          {getUserTypeDisplay()}
          <Edit className="ml-2 h-4 w-4" />
        </Button>
      )}
    </div>
  );
};
